import { DownloadOutlined } from '@mui/icons-material'
import { Box, Button, Stack, TablePagination } from '@mui/material'
import type {
  ExcelConversionFlow,
  ExcelConversionLog,
  GetExcelConversionLogsRequest,
  PlaceNode,
} from '@ulysses-inc/harami_api_client'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Header } from 'src/components/header/Header'
import { HeaderBackButtonMui } from 'src/components/header/HeaderBackButtonMui'
import { HeaderTitle } from 'src/components/header/HeaderTitle'
import { flattenNodes } from 'src/exShared/util/place/placeNode'
import { GlobalNavigation } from 'src/features/globalNavigation/GlobalNavigation'
import excelConversionFlowsOperations from 'src/state/ducks/excelConversionFlows/operations'
import excelConversionLogsOperations from 'src/state/ducks/excelConversionLogs/operations'
import type { GetExcelConversionLogsFilter } from 'src/state/ducks/excelConversionLogs/types'
import placesOperations from 'src/state/ducks/places/operations'
import type { RootState } from 'src/state/store'
import Loading from '../../components/loading/Loading'
import { useListFilter } from '../../hooks/filter/useListFilter'
import { useMutationDownloadExcelConversionLogFile } from './api/useMutationDownloadExcelConversionLogFile'
import ExcelConversionLogFilterDropDown from './ExcelConversionLogFilterDropDown'
import ExcelConversionLogList from './ExcelConversionLogList'

interface StateProps {
  logs: ExcelConversionLog[]
  flows: ExcelConversionFlow[]
  isLoading: boolean
  count: number
  places: PlaceNode[]
  placeNodes: PlaceNode[]
  filter?: GetExcelConversionLogsFilter
  request: GetExcelConversionLogsRequest
  isLoadingPlace: boolean
}

interface DispatchProps {
  getLogs: (
    request: GetExcelConversionLogsRequest,
    filter?: GetExcelConversionLogsFilter,
  ) => void
  getFilterData: () => void
  changeLogsPage: (
    page: number,
    pageSize?: number,
    filter?: GetExcelConversionLogsFilter,
  ) => void
  changeLogsSize: (
    pageSize: number,
    filter?: GetExcelConversionLogsFilter,
  ) => void
  updatePagination: (limit: number, offset: number) => void
  goFlows: () => void
  getExcelConversionLogFiles: (fileUUIDs: string[]) => void
}

type ExcelConversionLogsProps = StateProps & DispatchProps

const useStateProps = (): StateProps => {
  return {
    logs: useSelector(
      (state: RootState) =>
        state.excelConversionLogsState.excelConversionLogs.excelConversionLogs,
    ),
    flows: useSelector(
      (state: RootState) =>
        state.excelConversionFlowsState.excelConversionFlows
          .excelConversionFlows,
    ),
    isLoading: useSelector(
      (state: RootState) =>
        state.excelConversionLogsState.excelConversionLogs.isLoading,
    ),
    count: useSelector(
      (state: RootState) =>
        state.excelConversionLogsState.excelConversionLogs.count,
    ),
    filter: useSelector(
      (state: RootState) =>
        state.excelConversionLogsState.excelConversionLogs.filter,
    ),
    request: useSelector(
      (state: RootState) =>
        state.excelConversionLogsState.excelConversionLogs.request,
    ),
    places: useSelector((state: RootState) => state.placesState.places.places),
    placeNodes: useSelector((state: RootState) =>
      flattenNodes(state.placesState.placeGroups.nodes),
    ),
    isLoadingPlace: useSelector(
      (state: RootState) =>
        state.placesState.places.isLoading ||
        state.placesState.placeGroups.isLoading,
    ),
  }
}

const useDispatchProps = (): DispatchProps => {
  const dispatch = useDispatch()
  const history = useHistory()
  return {
    getLogs: (
      request: GetExcelConversionLogsRequest,
      filter?: GetExcelConversionLogsFilter,
    ) => {
      excelConversionLogsOperations.getExcelConversionLogs(
        dispatch,
        request,
        filter,
      )
    },
    changeLogsPage: (
      page: number,
      pageSize?: number,
      filter?: GetExcelConversionLogsFilter,
    ) => {
      excelConversionLogsOperations.changeExcelConversionLogsPage(
        dispatch,
        page,
        pageSize,
        filter,
      )
    },
    changeLogsSize: (
      pageSize: number,
      filter?: GetExcelConversionLogsFilter,
    ) => {
      excelConversionLogsOperations.changeExcelConversionLogsSize(
        dispatch,
        pageSize,
        filter,
      )
    },
    updatePagination: (limit: number, offset: number) => {
      excelConversionLogsOperations.updatePagination(dispatch, limit, offset)
    },
    getFilterData: () => {
      excelConversionFlowsOperations.getExcelConversionFlows(dispatch, {})
      placesOperations.getPlaces(dispatch, {})
      placesOperations.getPlaceGroups(dispatch)
    },
    goFlows: () => {
      history.push('/excelConversionFlows')
    },
    getExcelConversionLogFiles: (fileUUIDs: string[]) => {
      excelConversionLogsOperations.getExcelConversionLogFiles(
        dispatch,
        fileUUIDs,
      )
    },
  }
}

const ExcelConversionLogsContainer: React.FC = () => {
  const stateProps = useStateProps()
  const dispatchProps = useDispatchProps()
  const props = { ...stateProps, ...dispatchProps }
  return <ExcelConversionLogsScene {...props} />
}

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100]

const ExcelConversionLogsScene: React.FC<ExcelConversionLogsProps> = (
  props: ExcelConversionLogsProps,
) => {
  const { mutate: downloadSingleFile } =
    useMutationDownloadExcelConversionLogFile()
  const [chooseLogFileUUIDs, setChooseLogFileUUIDs] = useState<{
    [key: string]: string
  }>({})
  const [logGetTrigger, setLogGetTrigger] = useState<boolean>(false)
  const { filters, setListFilter } = useListFilter()
  const rowsPerPage = ROWS_PER_PAGE_OPTIONS.includes(props.request.limit ?? 25)
    ? (props.request.limit ?? 25)
    : 25

  useEffect(() => {
    if (!ROWS_PER_PAGE_OPTIONS.includes(props.request.limit ?? 25)) {
      props.changeLogsSize(25, props.filter)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.request.limit])

  useEffect(() => {
    props.getFilterData()
    // eslint-disable-next-line react-hooks/set-state-in-effect -- useEffect内でのsetState呼び出しは既存設計のため、リファクタリング時に修正する
    setLogGetTrigger(!logGetTrigger)
    if (filters) {
      const excelConversionLogsFilter = filters.excelConversionLogs
      props.updatePagination(
        excelConversionLogsFilter?.pagination?.limit ?? 25,
        excelConversionLogsFilter?.pagination?.offset ?? 0,
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setListFilter({
      ...(filters ?? {}),
      excelConversionLogs: {
        pagination: props.request,
        filter: filters?.excelConversionLogs?.filter,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.request.limit, props.request.offset])

  // MUI TablePagination は 0 オリジン
  const currentPage =
    props.request.offset === undefined ||
    props.request.limit === undefined ||
    props.request.limit === 0
      ? 0
      : props.request.offset / props.request.limit

  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    // ⚠️ changeLogsPage の第一引数は 1 オリジン
    props.changeLogsPage(newPage + 1, rowsPerPage, props.filter)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const size = parseInt(event.target.value, 10)
    props.changeLogsSize(size, props.filter)
  }

  return (
    // TODO: サイドメニュー描写の責務を`Routes.tsx`に移植する
    <GlobalNavigation
      resetPagination={() => {
        setListFilter({
          ...(filters ?? {}),
          excelConversionLogs: {
            pagination: { limit: 25, offset: 0 },
            filter: filters?.excelConversionLogs?.filter,
          },
        })
      }}
    >
      <Stack>
        <Box
          sx={{
            height: 72,
            width: '100%',
            alignItems: 'center',
            boxSizing: 'border-box',
            display: 'block',
            flexDirection: 'row',
          }}
        >
          <Header>
            <HeaderBackButtonMui onClick={() => props.goFlows()} />
            <HeaderTitle title="ダウンロードファイル一覧" />
          </Header>
        </Box>
        <Box
          width="100%"
          display="flex"
          justifyContent="space-between"
          sx={{ p: 2 }}
        >
          <ExcelConversionLogFilterDropDown
            flows={props.flows}
            places={props.places}
            placeNodes={props.placeNodes}
            getExcelConversionLogs={(filter?: GetExcelConversionLogsFilter) => {
              props.getLogs(props.request, filter)
            }}
            isLoadingPlace={props.isLoadingPlace}
            logGetTrigger={logGetTrigger}
            request={props.request}
          />
          <Button
            disabled={Object.keys(chooseLogFileUUIDs).length === 0}
            onClick={() => {
              const fileUUIDs = Object.values(chooseLogFileUUIDs)
              const [singleFileUUID] = fileUUIDs
              if (fileUUIDs.length === 1 && singleFileUUID !== undefined) {
                downloadSingleFile({ fileUUID: singleFileUUID })
                return
              }
              props.getExcelConversionLogFiles(fileUUIDs)
            }}
            variant="contained"
            color="primary"
            startIcon={<DownloadOutlined />}
          >
            一括ダウンロード
          </Button>
        </Box>
        {props.isLoading || props.isLoadingPlace ? (
          <Loading />
        ) : (
          <ExcelConversionLogList
            logs={props.logs}
            goFlows={props.goFlows}
            chooseLogUUIDs={chooseLogFileUUIDs}
            setChooseLogUUIDs={setChooseLogFileUUIDs}
          />
        )}
        <TablePagination
          component="div"
          count={props.count}
          page={currentPage}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          labelRowsPerPage="1ページあたり:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}〜${to} (${count !== -1 ? count : '不明'} 件中)`
          }
          sx={{
            '& .MuiTablePagination-selectLabel': { margin: 0 },
            '& .MuiTablePagination-displayedRows': { margin: 0 },
          }}
        />
      </Stack>
    </GlobalNavigation>
  )
}

export default ExcelConversionLogsContainer
